import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { CopyIcon } from "@components/Icons";
import Spin from "@components/UI/Spin";
import SeverityBadge from "./SeverityBadge";

const displayValue = (value, fallback) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  return value;
};

/**
 * Detail card (Component Information + Version Analysis + Additional).
 * CVE/Severity title is rendered by parent Region 2.
 */
const CveDetailPanel = ({
  cveItem,
  evidence,
  loading = false,
  versionAnalysisLoading = false,
  "data-testid": dataTestId = "cve-detail-panel",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const na = t("common.n-a", "N/A");
  const componentName = displayValue(evidence?.componentName, na);
  const installedVersion = displayValue(evidence?.installedVersion, na);
  const componentLabel =
    componentName === na ? na : `${componentName} (${displayValue(evidence?.installedVersion, "-")})`;
  const showWarning = evidence?.withinAffectedRange === true;
  const matches = Array.isArray(evidence?.matches) ? evidence.matches : [];

  const handleCopyComponent = async () => {
    try {
      await navigator.clipboard.writeText(componentLabel);
    } catch {
      // ignore clipboard errors in unsupported environments
    }
  };

  if (!cveItem) {
    return (
      <div
        className={`flex h-full items-center justify-center rounded-lg ${colors.bgModal} ${colors.textSecondary}`}
        data-testid={dataTestId}
      >
        {t("common.no-data-to-display")}
      </div>
    );
  }

  return (
    <div
      className={`flex h-full min-h-0 flex-col rounded-lg border ${colors.borderPrimary} ${colors.bgModal}`}
      data-testid={dataTestId}
    >
      <div className="px-4 py-3 flex-shrink-0">
        <h4 className={`text-sm font-semibold ${colors.textPrimary}`}>
          {t("status.vex-component-information")}
        </h4>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-5">
        {loading ? (
          <div className={`flex items-center justify-center ${colors.textSecondary}`}>
            <Spin />
          </div>
        ) : (
          <>
            <section data-testid={`${dataTestId}-component-information`}>
              <dl className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <dt className={`w-40 flex-shrink-0 ${colors.textSecondary}`}>{t("status.vex-component")}</dt>
                  <dd className={`flex items-center gap-2 min-w-0 ${colors.textPrimary}`}>
                    <span className="break-all">{componentLabel}</span>
                    {componentName !== na && (
                      <button
                        type="button"
                        onClick={handleCopyComponent}
                        title={t("common.copy", "Copy")}
                        aria-label={t("common.copy", "Copy")}
                        className={`${colors.textActive} hover:opacity-80 flex-shrink-0`}
                        data-testid={`${dataTestId}-copy-component`}
                      >
                        <CopyIcon size={14} />
                      </button>
                    )}
                  </dd>
                </div>
              </dl>
            </section>

            <section data-testid={`${dataTestId}-cve-information`}>
              <h4 className={`text-sm font-semibold mb-3 ${colors.textPrimary}`}>
                {t("status.vex-cve-information")}
              </h4>
              <dl className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <dt className={`w-40 flex-shrink-0 ${colors.textSecondary}`}>{t("status.cvss-version")}</dt>
                  <dd className={colors.textPrimary}>
                    {displayValue(cveItem?.cvss_version ?? cveItem?.cvssVersion, na)}
                  </dd>
                </div>
                <div className="flex items-start gap-2">
                  <dt className={`w-40 flex-shrink-0 ${colors.textSecondary}`}>{t("status.severity")}</dt>
                  <dd className={colors.textPrimary}>
                    {cveItem?.severity ? (
                      <SeverityBadge severity={cveItem.severity} data-testid={`${dataTestId}-severity`} />
                    ) : (
                      na
                    )}
                  </dd>
                </div>
                <div className="flex items-start gap-2">
                  <dt className={`w-40 flex-shrink-0 ${colors.textSecondary}`}>{t("status.score")}</dt>
                  <dd className={colors.textPrimary}>{displayValue(cveItem?.score, na)}</dd>
                </div>
              </dl>
            </section>

            <section data-testid={`${dataTestId}-version-analysis`}>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h4 className={`text-sm font-semibold ${colors.textPrimary}`}>
                  {t("status.vex-version-analysis")}
                </h4>
              </div>
              {versionAnalysisLoading ? (
                <div className={`flex items-center justify-center py-4 ${colors.textSecondary}`}>
                  <Spin />
                </div>
              ) : (
                <>
                  <dl className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <dt className={`w-48 flex-shrink-0 ${colors.textSecondary}`}>
                        {t("status.vex-installed-version")}
                      </dt>
                      <dd className={`min-w-0 ${colors.textPrimary}`}>
                        <span className="break-all">{installedVersion}</span>
                      </dd>
                    </div>
                  </dl>

                  <div
                    className={`mt-3 overflow-hidden rounded-md border ${colors.borderPrimary}`}
                    data-testid={`${dataTestId}-version-ranges`}
                  >
                    <div
                      className={`grid grid-cols-2 gap-2 border-b px-3 py-2 text-xs font-semibold ${colors.textSecondary} ${colors.borderPrimary}`}
                    >
                      <span>{t("status.vex-affected-version-range")}</span>
                      <span>{t("status.vex-fixed-version-range")}</span>
                    </div>
                    {matches.length === 0 ? (
                      <div className={`grid grid-cols-2 gap-2 px-3 py-2 text-sm ${colors.textSecondary}`}>
                        <span>{na}</span>
                        <span>{na}</span>
                      </div>
                    ) : (
                      matches.map((match, index) => (
                        <div
                          key={`${match.criteria || "match"}-${index}`}
                          className={`grid grid-cols-2 gap-2 px-3 py-2 text-sm ${colors.textPrimary} ${
                            index < matches.length - 1 ? `border-b ${colors.borderPrimary}` : ""
                          }`}
                        >
                          <span className="break-all">{displayValue(match.affectedVersionRange, na)}</span>
                          <span className="break-all">{displayValue(match.fixedVersion, na)}</span>
                        </div>
                      ))
                    )}
                  </div>

                  {showWarning && (
                    <div
                      className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-200"
                      data-testid={`${dataTestId}-affected-warning`}
                    >
                      <span aria-hidden="true">⚠</span>
                      <span>{t("status.vex-installed-in-affected-range")}</span>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default CveDetailPanel;
